import { 
  LayoutDashboard, 
  BookOpen, 
  Users, 
  FileText, 
  Package, 
  ShoppingCart, 
  Monitor, 
  UserCheck, 
  Clock,
  KanbanSquare,
  BarChart2,
  CreditCard,
  MessageSquare,
  TrendingUp,
  AlertTriangle,
  Building2,
  CheckCircle,
  Timer,
  Zap,
  PlusCircle,
  Truck,
  Moon,
  Upload,
  type LucideIcon 
} from 'lucide-react';
import type { Permission } from './rolePermissions';

export interface NavItem {
  label: string;
  icon: LucideIcon;
  path: string;
  moduleCode: string;
  permission: Permission;
  badge?: string;
}

export interface NavGroup {
  label: string;
  color: string;
  items: NavItem[];
}

export const NAVIGATION_CONFIG: NavGroup[] = [
  {
    label: "Dashboard",
    color: "text-slate-600",
    items: [
      { 
        label: "Dashboard", 
        icon: LayoutDashboard, 
        path: "/dashboard",
        moduleCode: "dashboard",
        permission: "dashboard.view" // Baseline access for every authenticated tenant user
      },
    ]
  },
  {
    label: "SALES",
    color: "text-blue-600",
    items: [
      { 
        label: "Point of Sale", 
        icon: Monitor, 
        path: "/pos", 
        moduleCode: "pos", 
        permission: "pos.access" 
      },
      { 
        label: "Quick Sale", 
        icon: Zap, 
        path: "/pos/quick", 
        moduleCode: "pos", 
        permission: "pos.access" 
      },
      { 
        label: "Close Day", 
        icon: Moon, 
        path: "/pos/close-day", 
        moduleCode: "pos", 
        permission: "pos.access" 
      },
      { 
        label: "Invoices", 
        icon: FileText, 
        path: "/invoices", 
        moduleCode: "invoices", 
        permission: "invoices.view" 
      },
      { 
        label: "Partners", 
        icon: Users, 
        path: "/partners", 
        moduleCode: "invoices", 
        permission: "partners.view" 
      },
    ]
  },
  {
    label: "FINANCE",
    color: "text-emerald-600",
    items: [
      { 
        label: "Digital Khata", 
        icon: BookOpen, 
        path: "/khata", 
        moduleCode: "khata", 
        permission: "khata.view" 
      },
      { 
        label: "Expenses", 
        icon: CreditCard, 
        path: "/expenses", 
        moduleCode: "expenses", 
        permission: "expenses.view" 
      },
      {
        label: "Business Reports",
        icon: BarChart2,
        path: "/reports",
        moduleCode: "reports",
        permission: "reports.view",
        badge: "New"
      },
      {
        label: "Aging & Analytics",
        icon: TrendingUp,
        path: "/reports/aging",
        moduleCode: "reports",
        permission: "reports.view",
        badge: "New"
      },
    ]
  },
  {
    label: "INVENTORY",
    color: "text-amber-600",
    items: [
      { 
        label: "Inventory", 
        icon: Package, 
        path: "/inventory", 
        moduleCode: "inventory", 
        permission: "inventory.view" 
      },
      { 
        label: "Purchase", 
        icon: ShoppingCart, 
        path: "/purchase", 
        moduleCode: "purchase", 
        permission: "purchase.view" 
      },
      { 
        label: "Damaged Goods", 
        icon: AlertTriangle, 
        path: "/damaged-goods", 
        moduleCode: "inventory", 
        permission: "inventory.view"
      },
      { 
        label: "Expiry Tracking", 
        icon: Timer, 
        path: "/expiry", 
        moduleCode: "inventory", 
        permission: "inventory.view"
      },
      { 
        label: "Quick Add Product", 
        icon: PlusCircle, 
        path: "/inventory/quick-add", 
        moduleCode: "inventory", 
        permission: "inventory.edit"
      },
      { 
        label: "Quick Receive Stock", 
        icon: Truck, 
        path: "/inventory/quick-receive", 
        moduleCode: "inventory", 
        permission: "inventory.edit"
      },
      { 
        label: "Bulk Import", 
        icon: Upload, 
        path: "/inventory/bulk-import", 
        moduleCode: "inventory", 
        permission: "inventory.edit"
      },
    ]
  },
  {
    label: "MY WORK",
    color: "text-indigo-600",
    items: [
      { 
        label: "My Attendance", 
        icon: Clock, 
        path: "/hr/my-attendance", 
        moduleCode: "hr", 
        permission: "self.work" 
      },
      { 
        label: "My Leaves", 
        icon: FileText, 
        path: "/hr/my-leaves", 
        moduleCode: "hr", 
        permission: "self.work" 
      },
      { 
        label: "My Payslips", 
        icon: CreditCard, 
        path: "/hr/my-payslips", 
        moduleCode: "hr", 
        permission: "self.work" 
      },
      { 
        label: "HR Assistance",
        icon: MessageSquare,
        path: "/hr/assistance", 
        moduleCode: "hr", 
        permission: "self.work" 
      },
    ]
  },
  {
    label: "TEAM & HR",
    color: "text-violet-600",
    items: [
      { 
        label: "HR Management", 
        icon: UserCheck, 
        path: "/hr", 
        moduleCode: "hr", 
        permission: "hr.view" 
      },
      {
        label: "Projects",
        icon: KanbanSquare,
        path: "/projects",
        moduleCode: "projects",
        permission: "projects.view"
      },
      {
        label: "Timesheets",
        icon: Clock,
        path: "/timesheets",
        moduleCode: "projects",
        permission: "projects.view",
        badge: "New"
      },
    ]
  },
  {
    label: "ADMIN",
    color: "text-slate-600",
    items: [
      {
        label: "Organization",
        icon: Building2,
        path: "/settings/organization",
        moduleCode: "settings",
        permission: "team.manage"
      },
      {
        label: "Plans & Billing",
        icon: CreditCard,
        path: "/billing",
        moduleCode: "settings",
        permission: "settings.view"
      },
      {
        label: "Approval Dashboard",
        icon: BarChart2,
        path: "/approval-dashboard",
        moduleCode: "settings",
        permission: "settings.view"
      },
      {
        label: "Approval Inbox",
        icon: CheckCircle,
        path: "/approval-inbox",
        moduleCode: "settings",
        permission: "settings.view"
      },
      {
        label: "Approval Analytics",
        icon: TrendingUp,
        path: "/approval-analytics",
        moduleCode: "settings",
        permission: "settings.view"
      },
    ]
  },
];

export const COUNTER_MODE_ALLOWED_PATHS = new Set([
  '/dashboard',
  '/pos/quick',
  '/pos/close-day',
  '/inventory',
  '/inventory/quick-add',
  '/inventory/quick-receive',
  '/inventory/bulk-import',
  '/expiry',
  '/khata',
  '/partners',
  '/reports',
]);

export function filterCounterModeNavigation(groups: NavGroup[], enabled: boolean): NavGroup[] {
  if (!enabled) return groups;
  return groups
    .map((group) => ({ ...group, items: group.items.filter((item) => COUNTER_MODE_ALLOWED_PATHS.has(item.path)) }))
    .filter((group) => group.items.length > 0);
}

export const GROCERY_ALLOWED_PATHS = new Set([
  '/dashboard',
  '/pos/quick',
  '/pos/close-day',
  '/khata',
  '/inventory',
  '/inventory/quick-add',
  '/inventory/quick-receive',
  '/reports',
]);

export function filterGroceryModeNavigation(groups: NavGroup[], enabled: boolean): NavGroup[] {
  if (!enabled) return groups;
  return groups
    .map((group) => ({ ...group, items: group.items.filter((item) => GROCERY_ALLOWED_PATHS.has(item.path)) }))
    .filter((group) => group.items.length > 0);
}
